import Axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseTransformer,
} from 'axios';
import humps from 'humps';

import config from '@config';
import * as interceptors from './interceptors';
import * as controllers from './controllers';
import type { IInternalAxiosRequestConfig } from './utils';
import {
  IForgotPasswordRequest,
  IProfileResponse,
  ISignInBodyRequest,
  ISuccessEmptyResponse,
  ITokensResponse,
  ISetNewPasswordRequest,
  ISignUpBodyRequest,
  IVerifySignUpDataBodyRequest,
  IQuestTopicResponse,
  IQuestSelectRequest,
  IPhotoResponse,
  IUpdateProfileBodyRequest,
  UpdateProfileBodyResponseType,
  ICategoriesResponse,
  IUpdatePasswordRequest,
  IRestoreAccountBodyRequest,
  ITutorialsResponse,
  ITutorialsBodyRequest,
  IPaginationRequestParams,
  IEnquiryTypeResponse,
  IContactUsMessageBodyRequest,
  IUserPublicResponse,
  IWatchProgressResponse,
  ITutorialResponse,
  IIdRequestParams,
  IRelatedTutorialsParamsRequest,
  ITutorialsRelatedResponse,
  IRefreshTokenRequest,
  ITutorialUpdateSaveStatusParams,
  IUpdateLikeStatusParams,
  ITutorialSubtitlesResponse,
  ITutorialSubtitlesRequestParams,
  ITutorialTimestampResponse,
  IUsersSuggestionStatusRequest,
  IFriendshipUsersRequest,
  IUsersResponse,
  IToggleFollowRequest,
  IPushNotificationsTokenRequest,
  IGetReferrerVideoResponse,
  NotificationTokenActionEnum,
  NotificationsResponseType,
  ICountResponse,
  ISetNotificationsAsReadRequestParams,
  IContactUsTicketBodyRequest,
  IShortLinkRequestParams,
  IShortLinkResponse,
  ITariffPlansResponse,
  IConversationsResponse,
  IIdResponse,
  IChangeTariffPlanRequestParams,
  ICreateSubscriptionPaymentIntentRequestParams,
  IPaymentClientSecretResponse,
  IConfirmChangeTariffPlanRequestParams,
  IMessagesResponse,
  ISendMessageResponse,
  ISendMessageRequestParams,
  ICreatePostParams,
  IPostPhotoResponse,
  IPostsResponse,
  IReportReasonTypeResponse,
  ISendReportBodyRequest,
  IPostVideoResponse,
  ITariffPlansRequestParams,
  IConversationResponse,
  ISearchCommonResponse,
  ICommentsResponse,
  ICreateCommentParams,
  ICommentIdParams,
  ICommentsRequestParams,
  IQueryRequestParams,
  IPostResponse,
  ITutorialSaveWatchTimeParams,
  IValidateCouponCodeResponse,
  IValidateCouponCodeRequestParams,
  IPostLikesResponse,
  IUpdateCommentParams,
  ISignUpResponse,
  IUpdatePostParams,
  ITutorialsSearchResponse,
  IUsersSearchResponse,
  ISendTrackFeedbackParams,
  ILastFeedbackResponse,
  ITagsResponse,
  ITagsRequestParams,
  ISignInResponse,
  ISectionsResponse,
  ICreateCreditsPaymentIntentRequestParams,
  ICreditPricesOffers,
  ISignUpConfirmResponse,
  IConfirmPaymentIntentRequestParams,
  ILegacyPaymentClientSecretResponse,
  IPaymentMethodsResponse,
  IPaymentMethodResponse,
  ISetupPaymentMethodResponse,
  IConfirmPaymentMethodRequestParams,
  IIdOfStingTypeRequestParams,
  IPaymentMethodIdResponse,
  ISetSkillLevelRequest,
  ISkillLevelQuestResponse,
  IPathRequestParams,
  ICourseBaseResponse,
  ICoursesResponse,
  ICourseLessonsResponse,
  ICourseTutorialMarkAsDoneResponse,
  ICourseTutorialMarkAsDoneRequestParams,
  ICourseSetCurrentTutorialRequestParams,
  ICourseLessonRequestParams,
  IGenericItemsResponse,
} from './types';
import { AnalyticsService, TokenService } from '@services';

export function createCancelSource() {
  return new AbortController();
}

/**
 * This is the instance of axios with built-in camelize/decamelize.
 * This is done in order to transform all the snake_case into camelCase
 * so when server responds with snake_case fields we transform it into camelCase.
 * But also we're allowed to use camelCase in our request body, it'll transform
 * it to snake_case automatically
 */
const axiosConfig: AxiosRequestConfig = {
  baseURL: config.API_URL,
  transformResponse: [
    ...(Axios.defaults
      .transformResponse as AxiosResponseTransformer[]),
    (data) => humps.camelizeKeys(data),
  ],
  // transformRequest: [
  //   (data) => humps.decamelizeKeys(data),
  //   ...Axios.defaults.transformRequest,
  // ],
};

const axios = Axios.create(axiosConfig);

// Instantiate the request interceptor
axios.interceptors.request.use(interceptors.requestFulfilled);

// Instantiate the response interceptor
axios.interceptors.response.use(
  interceptors.responseFulfilled,
  interceptors.responseRejected,
);

// Instantiate the refresh token renewal controller
export const refreshTokenRenewalController =
  controllers.createRefreshTokenRenewalController(axios, (tokens) =>
    Auth.refreshToken(tokens),
  );

export const Auth = {
  refreshToken(params: IRefreshTokenRequest) {
    return axios.post<
      ITokensResponse,
      AxiosResponse<ITokensResponse, IRefreshTokenRequest>,
      IRefreshTokenRequest
    >('/api/v1/identity/refresh-token', params, {
      // To prevent the interceptors loop we need to use skipAuthRefresh
      // on our refreshing call
      skipAuthRefresh: true,
    } as IInternalAxiosRequestConfig);
  },

  signIn(params: ISignInBodyRequest) {
    return axios.post<
      ISignInResponse,
      AxiosResponse<ISignInResponse, ISignInBodyRequest>,
      ISignInBodyRequest
    >('/api/v1/identity/login', params);
  },

  forgotPassword(params: IForgotPasswordRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, IForgotPasswordRequest>,
      IForgotPasswordRequest
    >('/api/v1/identity/forgot-password', params);
  },

  setNewPassword(params: ISetNewPasswordRequest) {
    return axios.post<
      ITokensResponse,
      AxiosResponse<ITokensResponse, ISetNewPasswordRequest>,
      ISetNewPasswordRequest
    >('/api/v1/identity/restore-password', params);
  },

  updatePassword(params: IUpdatePasswordRequest) {
    return axios.put<
      ITokensResponse,
      AxiosResponse<ITokensResponse, IUpdatePasswordRequest>,
      IUpdatePasswordRequest
    >('/api/v1/identity/reset-password', params);
  },

  signUp(params: ISignUpBodyRequest) {
    return axios.post<
      ISignUpResponse,
      AxiosResponse<ISignUpResponse, ISignUpBodyRequest>,
      ISignUpBodyRequest
      // TODO: return back /api/v1/identity/register (BE issue)
    >('/api/v1/identity/register3', params);
  },

  signUpConfirm(params: IConfirmPaymentIntentRequestParams) {
    return axios.post<
      ISignUpConfirmResponse,
      AxiosResponse<
        ISignUpConfirmResponse,
        IConfirmPaymentIntentRequestParams
      >,
      IConfirmPaymentIntentRequestParams
    >('/api/v1/identity/subscription/confirm', params);
  },

  verifySignUpData(params: IVerifySignUpDataBodyRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IVerifySignUpDataBodyRequest
      >,
      IVerifySignUpDataBodyRequest
    >('/api/v1/identity/pre-register', params);
  },

  deleteAccount(params: string) {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, string>,
      string
    >('/api/v1/identity', {
      headers: {
        // Axios will change Content-Type to 'text/plain'
        // For this request we need to specify only 'application/json'
        'Content-Type': 'application/json',
      },
      data: params,
    });
  },

  restoreAccount(params: IRestoreAccountBodyRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IRestoreAccountBodyRequest
      >,
      IRestoreAccountBodyRequest
    >('/api/v1/identity/restore-account', params);
  },

  signOut() {
    return axios.get<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >('/api/v1/identity/logout');
  },

  getReferrerVideo() {
    return axios.get<
      IGetReferrerVideoResponse,
      AxiosResponse<IGetReferrerVideoResponse>
    >('/api/v1/identity/referrer-video');
  },
};

export const Profile = {
  viewer() {
    return axios.get<
      IProfileResponse,
      AxiosResponse<IProfileResponse>
    >('/api/v1/profile');
  },

  updateProfile(
    params: IUpdateProfileBodyRequest,
    abortController?: AbortController,
  ) {
    return axios.put<
      UpdateProfileBodyResponseType,
      AxiosResponse<
        UpdateProfileBodyResponseType,
        IUpdateProfileBodyRequest
      >,
      IUpdateProfileBodyRequest
    >('/api/v1/profile', params, {
      signal: abortController?.signal,
    });
  },

  updatePhoto(params: FormData) {
    return axios.post<
      IPhotoResponse,
      AxiosResponse<IPhotoResponse>,
      FormData
    >('/api/v1/profile/photo', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  removePhoto() {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >('/api/v1/profile/photo');
  },

  getQuestTopics() {
    return axios.get<
      IQuestTopicResponse[],
      AxiosResponse<IQuestTopicResponse[]>
    >('/api/v1/profile/quest-topics');
  },

  getSelectedQuestTopics() {
    return axios.get<number[], AxiosResponse<number[]>>(
      '/api/v1/profile/quest-selected',
    );
  },

  questSelect(params: IQuestSelectRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, IQuestSelectRequest>,
      IQuestSelectRequest
    >('/api/v1/profile/quest-selected', params);
  },

  getSkillLevelQuest() {
    return axios.get<
      ISkillLevelQuestResponse,
      AxiosResponse<ISkillLevelQuestResponse>
    >('/api/v1/profile/skill-level/start-question');
  },

  setSkillLevel(params: ISetSkillLevelRequest) {
    return axios.put<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, ISetSkillLevelRequest>,
      ISetSkillLevelRequest
    >('/api/v1/profile/skill-level/set-skill-level', params);
  },

  getWatchProgress() {
    return axios.get<
      IWatchProgressResponse,
      AxiosResponse<IWatchProgressResponse>
    >('/api/v1/profile/progress-watched');
  },

  searchUsers(
    {
      count,
      start,
      query,
    }: IPaginationRequestParams & IQueryRequestParams,
    abortController?: AbortController,
  ) {
    return axios.post<
      IUsersSearchResponse,
      AxiosResponse<
        IUsersSearchResponse,
        IPaginationRequestParams & IQueryRequestParams
      >,
      IPaginationRequestParams & IQueryRequestParams
    >(
      '/api/v1/profile/search',
      { query },
      {
        params: { count, start },
        signal: abortController?.signal,
      },
    );
  },

  getSharedData(userIds: number[]) {
    return axios.post<
      IUserPublicResponse[],
      AxiosResponse<IUserPublicResponse[], number[]>,
      number[]
    >('/api/v1/profile/shared-data', userIds);
  },

  getSharedDataById({ id }: IIdRequestParams) {
    return axios.get<
      IUserPublicResponse,
      AxiosResponse<IUserPublicResponse>
    >(`/api/v1/profile/${id}/shared-data`);
  },

  getSuggestedUsers() {
    return axios.get<
      IUserPublicResponse[],
      AxiosResponse<IUserPublicResponse[]>
    >('/api/v1/profile/follow-suggest');
  },

  saveUsersSuggestionStatus(params: IUsersSuggestionStatusRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IUsersSuggestionStatusRequest
      >,
      IUsersSuggestionStatusRequest
    >('/api/v1/profile/follow-suggest', params);
  },
};

export const ContactUs = {
  getEnquiryTypes() {
    return axios.get<
      IEnquiryTypeResponse[],
      AxiosResponse<IEnquiryTypeResponse[]>
    >('/api/v1/contact-us/enquiry-type');
  },

  sendMessage(params: IContactUsMessageBodyRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IContactUsMessageBodyRequest
      >,
      IContactUsMessageBodyRequest
    >('/api/v1/contact-us', params);
  },

  sendTicket(params: IContactUsTicketBodyRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IContactUsTicketBodyRequest
      >,
      IContactUsTicketBodyRequest
    >('/api/v1/contact-us/ticket', params);
  },
};

export const Hub = {
  getTutorials({
    count,
    start,
    ...body
  }: ITutorialsBodyRequest & IPaginationRequestParams) {
    return axios.post<
      ITutorialsResponse,
      AxiosResponse<ITutorialsResponse, ITutorialsBodyRequest>,
      ITutorialsBodyRequest
    >('/api/v1/hub/videos', body, { params: { count, start } });
  },

  getGenericItems({
    path,
    ...params
  }: IPaginationRequestParams & IPathRequestParams) {
    return axios.get<
      IGenericItemsResponse,
      AxiosResponse<IGenericItemsResponse>
    >(`/api/v1/hub/items/${path}`, { params: params });
  },

  getRelatedTutorials({
    id,
    ...params
  }: IIdRequestParams &
    IPaginationRequestParams &
    IRelatedTutorialsParamsRequest) {
    return axios.get<
      ITutorialsRelatedResponse,
      AxiosResponse<
        ITutorialsRelatedResponse,
        IIdRequestParams &
          IPaginationRequestParams &
          IRelatedTutorialsParamsRequest
      >,
      IIdRequestParams &
        IPaginationRequestParams &
        IRelatedTutorialsParamsRequest
    >(`/api/v1/hub/videos/${id}/related`, { params: params });
  },

  getTutorialById({ id }: IIdRequestParams) {
    return axios.get<
      ITutorialResponse,
      AxiosResponse<ITutorialResponse, IIdRequestParams>,
      IIdRequestParams
    >(`/api/v1/hub/videos/${id}`);
  },

  getCategories() {
    return axios.get<
      ICategoriesResponse,
      AxiosResponse<ICategoriesResponse>
    >('/api/v1/hub/categories');
  },

  updateSaveStatus(
    {
      id,
      status,
    }: IIdRequestParams & ITutorialUpdateSaveStatusParams,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/hub/videos/save/${id}/${status}`, null, {
      signal: abortController.signal,
    });
  },

  updateLikeStatus(
    { id, status }: IIdRequestParams & IUpdateLikeStatusParams,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/hub/videos/like/${id}/${status}`, null, {
      signal: abortController.signal,
    });
  },

  getSubtitles({
    id,
    language,
  }: IIdRequestParams & ITutorialSubtitlesRequestParams) {
    return axios.get<
      ITutorialSubtitlesResponse,
      AxiosResponse<ITutorialSubtitlesResponse>
    >(`/api/v1/hub/videos/${id}/subtitles`, { params: { language } });
  },

  saveView({ id }: IIdRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/hub/videos/${id}`);
  },

  /**
   * Save watch time for a tutorial using fetch instead of axios.
   *
   * We use fetch with the 'keepalive' option set to true because we often need to send this request
   * when the user is closing the page or the app. Regular axios POST requests may not have enough time
   * to complete in these scenarios. The 'keepalive' option ensures that the request is reliably sent
   * even if the page/app is being closed.
   *
   * Axios doesn't support the 'keepalive' option.
   */
  async saveWatchTime(params: ITutorialSaveWatchTimeParams) {
    const tokens = await TokenService.get();
    const sessionId = AnalyticsService.getSessionId();

    return fetch(config.API_URL + '/api/v1/hub/videos/watch-time', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens?.accessToken}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Session-Id': sessionId?.toString() ?? '',
      },
      keepalive: true,
    });
  },

  getTimestamps({ id }: IIdRequestParams) {
    return axios.get<
      ITutorialTimestampResponse[],
      AxiosResponse<ITutorialTimestampResponse[]>
    >(`/api/v1/hub/videos/${id}/timestamps`);
  },

  searchTutorials(
    params: IPaginationRequestParams & IQueryRequestParams,
    abortController?: AbortController,
  ) {
    return axios.get<
      ITutorialsSearchResponse,
      AxiosResponse<
        ITutorialsSearchResponse,
        IPaginationRequestParams & IQueryRequestParams
      >,
      IPaginationRequestParams & IQueryRequestParams
    >('/api/v1/hub/videos/search', {
      params: params,
      signal: abortController?.signal,
    });
  },

  getComments({
    id,
    ...params
  }: IIdRequestParams & ICommentsRequestParams) {
    return axios.get<
      ICommentsResponse,
      AxiosResponse<ICommentsResponse, ICommentsRequestParams>,
      ICommentsRequestParams
    >(`/api/v1/hub/videos/${id}/comments`, { params: params });
  },

  createComment({
    id,
    ...params
  }: IIdRequestParams & ICreateCommentParams) {
    return axios.post<
      IIdResponse,
      AxiosResponse<IIdResponse, ICreateCommentParams>,
      ICreateCommentParams
    >(`/api/v1/hub/videos/${id}/comments`, params);
  },

  updateCommentLikeStatus(
    {
      id,
      status,
      commentId,
    }: IIdRequestParams & IUpdateLikeStatusParams & ICommentIdParams,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(
      `/api/v1/hub/videos/${id}/comments/like/${commentId}/${status}`,
      null,
      {
        signal: abortController.signal,
      },
    );
  },

  sendCommentReport({
    id,
    commentId,
    ...params
  }: IIdRequestParams & ISendReportBodyRequest & ICommentIdParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, ISendReportBodyRequest>,
      ISendReportBodyRequest
    >(
      `/api/v1/hub/videos/${id}/comments/${commentId}/report`,
      params,
    );
  },

  updateComment({
    id,
    commentId,
    ...params
  }: IIdRequestParams & IUpdateCommentParams & ICommentIdParams) {
    return axios.put<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, IUpdateCommentParams>,
      IUpdateCommentParams
    >(`/api/v1/hub/videos/${id}/comments/${commentId}`, params);
  },

  deleteComment({
    id,
    commentId,
  }: IIdRequestParams & ICommentIdParams) {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/hub/videos/${id}/comments/${commentId}`);
  },

  getTags(params?: ITagsRequestParams) {
    return axios.get<
      IPostsResponse,
      AxiosResponse<ITagsResponse, IPaginationRequestParams>,
      IPaginationRequestParams
    >('/api/v1/hub/videos/tags', { params: params });
  },

  getTagsByTutorialId({ id }: IIdRequestParams) {
    return axios.get<
      IPostsResponse,
      AxiosResponse<ITagsResponse, IPaginationRequestParams>,
      IPaginationRequestParams
    >(`/api/v1/hub/videos/tags-by-video/${id}`);
  },

  getTutorialStreamSourceUrl({ id }: IIdRequestParams) {
    return axios.getUri({ url: `/api/v1/hub/videos/${id}/source` });
  },

  getTutorialStreamSource({ id }: IIdRequestParams) {
    return axios.get(this.getTutorialStreamSourceUrl({ id }));
  },
};

export const Tracks = {
  sendTrackFeedback(params?: ISendTrackFeedbackParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >('/api/v1/tracks/feedback-request', params);
  },

  getTrackFeedbacks() {
    return axios.post<
      ILastFeedbackResponse,
      AxiosResponse<ILastFeedbackResponse>
    >('/api/v1/tracks/last-feedback');
  },
};

export const Friendships = {
  getFriendshipUsers({
    id,
    friendshipType,
    ...params
  }: IFriendshipUsersRequest) {
    return axios.get<
      IUsersResponse,
      AxiosResponse<IUsersResponse, IFriendshipUsersRequest>,
      IFriendshipUsersRequest
    >(`/api/v1/friendships/${id}/${friendshipType}`, {
      params: params,
    });
  },

  toggleFollow(
    { id, action }: IToggleFollowRequest,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/friendships/${id}/${action}`, null, {
      signal: abortController.signal,
    });
  },
};

export const Notifications = {
  addPushNotificationsToken(params: IPushNotificationsTokenRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IPushNotificationsTokenRequest
      >,
      IPushNotificationsTokenRequest
    >('/api/v1/notify/token', params, {
      params: { action: NotificationTokenActionEnum.Add },
    });
  },

  removePushNotificationsToken(
    params: IPushNotificationsTokenRequest,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IPushNotificationsTokenRequest
      >,
      IPushNotificationsTokenRequest
    >('/api/v1/notify/token', params, {
      params: { action: NotificationTokenActionEnum.Delete },
    });
  },

  getNotifications() {
    return axios.get<
      NotificationsResponseType,
      AxiosResponse<NotificationsResponseType>
    >('/api/v1/notify');
  },

  getCountOfNewNotifications() {
    return axios.get<ICountResponse, AxiosResponse<ICountResponse>>(
      '/api/v1/notify/count',
    );
  },

  setMultipleAsRead(params: ISetNotificationsAsReadRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        ISetNotificationsAsReadRequestParams
      >,
      ISetNotificationsAsReadRequestParams
    >('/api/v1/notify', params);
  },

  setAsRead(params: IIdRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/notify/${params.id}`);
  },
};

export const Linking = {
  shortLink(params: IShortLinkRequestParams) {
    return axios.post<
      IShortLinkResponse,
      AxiosResponse<IShortLinkResponse, IShortLinkRequestParams>,
      IShortLinkRequestParams
    >('/api/v1/linking/shortlink', params);
  },
};

export const Payments = {
  getTariffPlanPrices(params: ITariffPlansRequestParams) {
    return axios.post<
      ITariffPlansResponse,
      AxiosResponse<ITariffPlansResponse, ITariffPlansRequestParams>,
      ITariffPlansRequestParams
      // TODO: return back /api/v1/payment/prices2 (BE issue)
    >('/api/v1/payment/prices2', params);
  },

  getCreditsPricesOffers() {
    return axios.get<
      ICreditPricesOffers,
      AxiosResponse<ICreditPricesOffers>
    >('/api/v1/payment/credits');
  },

  getPaymentMethods() {
    return axios.get<
      IPaymentMethodsResponse,
      AxiosResponse<IPaymentMethodsResponse>
    >('/api/v1/profile/payments-methods');
  },

  getPaymentMethod({ id }: IIdOfStingTypeRequestParams) {
    return axios.get<
      IPaymentMethodResponse,
      AxiosResponse<IPaymentMethodResponse>
    >(`/api/v1/profile/payments-method/${id}`);
  },

  confirmPaymentMethod(params: IConfirmPaymentMethodRequestParams) {
    return axios.post<
      IPaymentMethodIdResponse,
      AxiosResponse<
        IPaymentMethodIdResponse,
        IConfirmPaymentMethodRequestParams
      >,
      IConfirmPaymentMethodRequestParams
    >('/api/v1/profile/payments-method/confirm', params);
  },

  setupPaymentMethod() {
    return axios.post<
      ISetupPaymentMethodResponse,
      AxiosResponse<ISetupPaymentMethodResponse>
    >('/api/v1/profile/payments-method/setup');
  },

  deletePaymentMethod(params: IIdOfStingTypeRequestParams) {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/profile/payments-method/${params.id}`);
  },

  updatePaymentMethod(
    { id }: IIdOfStingTypeRequestParams,
    abortController?: AbortController,
  ) {
    return axios.put<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/profile/payments-method/${id}`, undefined, {
      signal: abortController?.signal,
    });
  },

  cancelSubscription() {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >('/api/v1/payment/cancel');
  },

  changeTariffPlan(params: IChangeTariffPlanRequestParams) {
    return axios.post<
      ILegacyPaymentClientSecretResponse,
      AxiosResponse<
        ILegacyPaymentClientSecretResponse,
        IChangeTariffPlanRequestParams
      >,
      IChangeTariffPlanRequestParams
    >('/api/v1/payment/change2', params);
  },

  createSubscriptionPaymentIntent(
    params: ICreateSubscriptionPaymentIntentRequestParams,
  ) {
    return axios.post<
      IPaymentClientSecretResponse,
      AxiosResponse<
        IPaymentClientSecretResponse,
        ICreateSubscriptionPaymentIntentRequestParams
      >,
      ICreateSubscriptionPaymentIntentRequestParams
    >('/api/v1/payment/subscription/create', params);
  },

  confirmChangeTariffPlan(
    params: IConfirmChangeTariffPlanRequestParams,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<
        ISuccessEmptyResponse,
        IConfirmChangeTariffPlanRequestParams
      >,
      IConfirmChangeTariffPlanRequestParams
    >('/api/v1/payment/change/confirm', params);
  },

  validateCouponCode(params: IValidateCouponCodeRequestParams) {
    return axios.get<
      IValidateCouponCodeResponse[],
      AxiosResponse<
        IValidateCouponCodeResponse[],
        IValidateCouponCodeRequestParams
      >,
      IValidateCouponCodeRequestParams
    >('/api/v1/payment/coupon', { params });
  },

  createCreditsPaymentIntent(
    params: ICreateCreditsPaymentIntentRequestParams,
  ) {
    return axios.post<
      IPaymentClientSecretResponse,
      AxiosResponse<IPaymentClientSecretResponse>
    >('/api/v1/payment/credits/create', params);
  },

  confirmPayment(params: IConfirmPaymentIntentRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >('/api/v1/payment/confirm', params);
  },
};

export const Messaging = {
  getCountOfNewMessages() {
    return axios.get<ICountResponse, AxiosResponse<ICountResponse>>(
      '/api/v1/messages/count',
    );
  },

  getConversationById({ id }: IIdRequestParams) {
    return axios.get<
      IConversationResponse,
      AxiosResponse<IConversationResponse>
    >(`/api/v1/messages/conversation/${id}`);
  },

  getConversations(
    params?: IPaginationRequestParams & Partial<IQueryRequestParams>,
    abortController?: AbortController,
  ) {
    return axios.get<
      IConversationsResponse,
      AxiosResponse<IConversationsResponse>
    >('/api/v1/messages/list', {
      params: params,
      signal: abortController?.signal,
    });
  },

  getMessages({
    id,
    ...params
  }: IIdRequestParams & IPaginationRequestParams) {
    return axios.get<
      IMessagesResponse,
      AxiosResponse<IMessagesResponse>
    >(`/api/v1/messages/${id}`, { params: params });
  },

  sendMessage(params: ISendMessageRequestParams) {
    return axios.post<
      ISendMessageResponse,
      AxiosResponse<ISendMessageResponse, ISendMessageRequestParams>,
      ISendMessageRequestParams
    >('/api/v1/messages', params);
  },

  setAsRead(params: IIdRequestParams) {
    return axios.get<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/messages/mark-as-read/${params.id}`);
  },
};

export const Feed = {
  getPosts(params?: IPaginationRequestParams) {
    return axios.get<
      IPostsResponse,
      AxiosResponse<IPostsResponse, IPaginationRequestParams>,
      IPaginationRequestParams
    >('/api/v1/feed', { params: params });
  },

  getUserPosts({
    id,
    ...restParams
  }: IIdRequestParams & IPaginationRequestParams) {
    return axios.get<
      IPostsResponse,
      AxiosResponse<IPostsResponse, IPaginationRequestParams>,
      IPaginationRequestParams
    >(`/api/v1/feed/${id}/status`, { params: restParams });
  },

  getPostLikes({
    id,
    ...restParams
  }: IIdRequestParams & IPaginationRequestParams) {
    return axios.get<
      IPostLikesResponse,
      AxiosResponse<IPostLikesResponse, IPaginationRequestParams>,
      IPaginationRequestParams
    >(`/api/v1/feed/${id}/likes`, { params: restParams });
  },

  getPostById({ id }: IIdRequestParams) {
    return axios.get<
      IPostResponse,
      AxiosResponse<IPostResponse, IIdRequestParams>,
      IIdRequestParams
    >(`/api/v1/feed/${id}`);
  },

  updateLikeStatus(
    { id, status }: IIdRequestParams & IUpdateLikeStatusParams,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/feed/like/${id}/${status}`, null, {
      signal: abortController.signal,
    });
  },

  createPost(params: ICreatePostParams) {
    return axios.post<
      IIdResponse,
      AxiosResponse<IIdResponse, ICreatePostParams>,
      ICreatePostParams
    >('/api/v1/feed/post', params);
  },

  updatePost({
    id,
    ...params
  }: IIdRequestParams & IUpdatePostParams) {
    return axios.put<
      IIdResponse,
      AxiosResponse<IIdResponse, IUpdatePostParams>,
      IUpdatePostParams
    >(`/api/v1/feed/${id}/post`, params);
  },

  deletePost({ id }: IIdRequestParams) {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/feed/${id}/post`);
  },

  uploadPhoto(params: FormData, abortController?: AbortController) {
    return axios.put<
      IPostPhotoResponse,
      AxiosResponse<IPostPhotoResponse, FormData>,
      FormData
    >('/api/v1/feed/post/photo', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController?.signal,
    });
  },

  uploadVideo(params: FormData, abortController?: AbortController) {
    return axios.put<
      IPostVideoResponse,
      AxiosResponse<IPostVideoResponse, FormData>,
      FormData
    >('/api/v1/feed/post/video', params, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal: abortController?.signal,
    });
  },

  sendReport({
    id,
    ...params
  }: IIdRequestParams & ISendReportBodyRequest) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, ISendReportBodyRequest>,
      ISendReportBodyRequest
    >(`/api/v1/feed/${id}/report`, params);
  },

  getComments({
    id,
    ...params
  }: IIdRequestParams & ICommentsRequestParams) {
    return axios.get<
      ICommentsResponse,
      AxiosResponse<ICommentsResponse, ICommentsRequestParams>,
      ICommentsRequestParams
    >(`/api/v1/feed/${id}/comments`, { params: params });
  },

  createComment({
    id,
    ...params
  }: IIdRequestParams & ICreateCommentParams) {
    return axios.post<
      IIdResponse,
      AxiosResponse<IIdResponse, ICreateCommentParams>,
      ICreateCommentParams
    >(`/api/v1/feed/${id}/comments`, params);
  },

  updateCommentLikeStatus(
    {
      id,
      status,
      commentId,
    }: IIdRequestParams & IUpdateLikeStatusParams & ICommentIdParams,
    abortController: AbortController,
  ) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(
      `/api/v1/feed/${id}/comments/like/${commentId}/${status}`,
      null,
      {
        signal: abortController.signal,
      },
    );
  },

  sendCommentReport({
    id,
    commentId,
    ...params
  }: IIdRequestParams & ISendReportBodyRequest & ICommentIdParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, ISendReportBodyRequest>,
      ISendReportBodyRequest
    >(`/api/v1/feed/${id}/comments/${commentId}/report`, params);
  },

  updateComment({
    id,
    commentId,
    ...params
  }: IIdRequestParams & IUpdateCommentParams & ICommentIdParams) {
    return axios.put<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse, IUpdateCommentParams>,
      IUpdateCommentParams
    >(`/api/v1/feed/${id}/comments/${commentId}`, params);
  },

  deleteComment({
    id,
    commentId,
  }: IIdRequestParams & ICommentIdParams) {
    return axios.delete<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/feed/${id}/comments/${commentId}`);
  },
};

export const Reports = {
  getReasonTypes() {
    return axios.get<
      IReportReasonTypeResponse[],
      AxiosResponse<IReportReasonTypeResponse[]>
    >('/api/v1/report');
  },
};

export const Search = {
  searchCommon(
    params: IQueryRequestParams,
    abortController: AbortController,
  ) {
    return axios.get<
      ISearchCommonResponse,
      AxiosResponse<ISearchCommonResponse, IQueryRequestParams>,
      IQueryRequestParams
    >('/api/v1/search', {
      params: params,
      signal: abortController.signal,
    });
  },
};

export const Courses = {
  getCourseById({ id }: IIdRequestParams) {
    return axios.get<
      ICourseBaseResponse,
      AxiosResponse<ICourseBaseResponse>
    >(`/api/v1/courses/${id}`);
  },

  startCourse({ id }: IIdRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/courses/${id}/start`);
  },

  completeCourse({ id }: IIdRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/courses/${id}/complete`);
  },

  getGenericCourses({
    path,
    ...params
  }: IPaginationRequestParams & IPathRequestParams) {
    return axios.get<
      ICoursesResponse,
      AxiosResponse<ICoursesResponse>
    >(`/api/v1/courses/${path}`, { params: params });
  },

  getLessonsByCourseId({ id }: IIdRequestParams) {
    return axios.get<
      ICourseLessonsResponse,
      AxiosResponse<ICourseLessonsResponse>
    >(`/api/v1/courses/${id}/lessons`);
  },

  startLesson({ id, lessonId }: ICourseLessonRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/courses/${id}/lesson/${lessonId}/start`);
  },

  completeLesson({ id, lessonId }: ICourseLessonRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(`/api/v1/courses/${id}/lesson/${lessonId}/complete`);
  },

  markTutorialAsDone({
    courseId,
    tutorialId,
  }: ICourseTutorialMarkAsDoneRequestParams) {
    return axios.post<
      ICourseTutorialMarkAsDoneResponse,
      AxiosResponse<ICourseTutorialMarkAsDoneResponse>
    >(`/api/v1/courses/${courseId}/video/${tutorialId}/done`);
  },

  setCurrentTutorial({
    courseId,
    tutorialId,
  }: ICourseSetCurrentTutorialRequestParams) {
    return axios.post<
      ISuccessEmptyResponse,
      AxiosResponse<ISuccessEmptyResponse>
    >(
      `/api/v1/courses/${courseId}/video/${tutorialId}/current-tutorial`,
    );
  },
};

export const Sections = {
  getLatestSections(params?: IPaginationRequestParams) {
    return axios.get<
      ISectionsResponse,
      AxiosResponse<ISectionsResponse>
    >('/api/v2/section/latest', {
      params,
      // The backend expects arrays as ?query=1&query=2 (without square brackets).
      // By default, Axios sends ?query[]=1&query[]=2, so we use indexes: null to adjust the format.
      paramsSerializer: { indexes: null },
    });
  },
  getMyHubSections(params?: IPaginationRequestParams) {
    return axios.get<
      ISectionsResponse,
      AxiosResponse<ISectionsResponse>
    >('/api/v2/section/my-hub', {
      params,
      // The backend expects arrays as ?query=1&query=2 (without square brackets).
      // By default, Axios sends ?query[]=1&query[]=2, so we use indexes: null to adjust the format.
      paramsSerializer: { indexes: null },
    });
  },
};
